import { useCallback } from 'react';
import { logger } from '@/lib/logger';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { getAuthToken } from '@/lib/auth-token';
import { useAuth } from '@/lib/auth';
import { clearCachedSubscription } from '@/lib/subscription';

export interface SubscriptionDetails {
  isActive: boolean;
  tierName: string | null;
  stripe_price_id: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean | null;
  status?: string | null; // Add status field for trial detection
  stripe_customer_id?: string | null; // Customer ID for validation
  // Add other fields returned by the Edge Function as needed
}

const fetchSubscriptionStatus = async (): Promise<SubscriptionDetails | null> => {
  try {
    logger.debug('[fetchSubscriptionStatus] Starting fetch...');
    const token = await getAuthToken();
    if (!token) {
      // User not logged in, return free tier
      return {
          isActive: false,
          tierName: 'Free',
          stripe_price_id: null,
          current_period_end: null,
          cancel_at_period_end: null,
          status: null,
      };
    }

    // Use PostgREST RPC — pure DB read, no Edge Function needed
    const { data, error } = await supabase.rpc('get_user_subscription');

    if (error) {
      logger.warn(`Subscription API error:`, { error });
      // Return free tier on error
      return {
          isActive: false,
          tierName: 'Free',
          stripe_price_id: null,
          current_period_end: null,
          cancel_at_period_end: null,
          status: null,
      };
    }

    logger.debug('[fetchSubscriptionStatus] Edge function returned:', data as unknown as Record<string, any>);
    return data as unknown as SubscriptionDetails;
  } catch (error) {
    logger.error('Error fetching subscription status:', error);
    // Return free tier on any error
    return {
        isActive: false,
        tierName: 'Free',
        stripe_price_id: null,
        current_period_end: null,
        cancel_at_period_end: null,
        status: null,
    };
  }
};

export function useInvalidateSubscription() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return () => {
    if (user?.id) {
      // Clear module-level cache
      clearCachedSubscription();

      // Invalidate ALL subscription query keys
      queryClient.invalidateQueries({ queryKey: ['subscription', user.id] });
      queryClient.invalidateQueries({ queryKey: ['subscription-details', user.id] });
      queryClient.invalidateQueries({ queryKey: ['subscriptionStatus', user.id] });
    }
  };
}

export const useSubscriptionWithTier = () => {
  const { user, loading: isAuthLoading } = useAuth();
  const queryClient = useQueryClient();

  const queryKey = ['subscriptionStatus', user?.id];

  // React Query v5 requires object parameter syntax
  const {
    data,
    isLoading,
    isError,
    error
  } = useQuery({
    queryKey,
    queryFn: fetchSubscriptionStatus,
    enabled: !!user && !isAuthLoading,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    refetchOnWindowFocus: true
  });

  // Function to manually invalidate and refetch subscription
  // Clears ALL subscription caches to ensure consistent state across the app
  // Wrapped in useCallback to ensure stable reference for useEffect dependencies
  const refreshSubscription = useCallback(() => {
    // Clear module-level cache (critical for hasActiveSubscription/getUserSubscription)
    clearCachedSubscription();

    // Invalidate ALL subscription-related React Query keys
    queryClient.invalidateQueries({ queryKey: ['subscriptionStatus', user?.id] });
    queryClient.invalidateQueries({ queryKey: ['subscription', user?.id] });
    queryClient.invalidateQueries({ queryKey: ['subscription-details', user?.id] });
  }, [queryClient, user?.id]);

  return {
    subscription: data,
    isLoading: isLoading || isAuthLoading,
    isError,
    error,
    refreshSubscription,
    // Expose specific details for convenience, defaulting if data is null/undefined
    isActive: data?.isActive ?? false,
    tierName: data?.tierName ?? 'Free',
    current_period_end: data?.current_period_end,
    cancel_at_period_end: data?.cancel_at_period_end,
    stripe_price_id: data?.stripe_price_id,
    status: data?.status, // Expose status for trial detection
    stripe_customer_id: data?.stripe_customer_id, // Expose customer ID
  };
}; 